/**
 * MarketingActionsModal.tsx
 *
 * Replicates the legacy sendgrid-details.php modal.
 * Provides three actions: Save List, Send Email, Send SMS.
 * Reads the current page's filter state (passed as props) and calls the
 * resolveRecipients API endpoint — the single source of truth.
 *
 * Access: the host page shows the "Email / SMS List" button on Marketing Access
 * (permission 20), which covers Save List. Building a campaign off the list is
 * the narrower Marketing Campaigns permission (27) — without it the Send Email
 * and Send SMS actions are not offered and their screens are unreachable.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail, Phone, Users, Save, Send, MessageSquare,
  Search, ChevronLeft, Layout, X,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Textarea } from '@/components/ui/Textarea';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { PERMISSION } from '@/lib/permissions';
import { FtpTemplatePicker } from '@/components/ftp-editor/FtpTemplatePicker';
import type { FtpTemplateRow } from '@/components/ftp-editor/FtpTemplatePicker';
import SmsTemplatePickerModal from '@/components/sms-editor/SmsTemplatePickerModal';
import type { SmsMarketingTemplate } from '@/services/marketing.service';
import * as marketingSvc from '@/services/marketing.service';
import * as campaignsSvc from '@/services/campaigns.service';
import { apiClient } from '@/services/apiClient';

type ModalScreen = 'choice' | 'save-list' | 'send-email' | 'send-sms';

function formatDateTime(d: Date): string {
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function sourceLabel(sourceType: string): string {
  return sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
}

function formatPhone(raw: string | undefined | null): string {
  if (!raw) return '--';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

export interface RecipientsData {
  total: number;
  uniqueEmailCount: number;
  uniquePhoneCount: number;
  recipients: Array<{ email?: string; firstName?: string; lastName?: string; phone?: string }>;
}

export interface MarketingActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceType: string;
  filterConditions: Record<string, unknown>;
  filterPlus?: string[];
  externalRecipientsData?: RecipientsData | null;
  externalRecipientsLoading?: boolean;
  /** Names the default list / campaign ("Partell List - …"); defaults to the source type. */
  listLabel?: string;
}

export function MarketingActionsModal({
  isOpen,
  onClose,
  sourceType,
  filterConditions,
  filterPlus,
  externalRecipientsData,
  externalRecipientsLoading,
  listLabel,
}: MarketingActionsModalProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canBuildCampaigns = hasPermission(PERMISSION.MARKETING_CAMPAIGNS);
  const [screen, setScreen] = useState<ModalScreen>('choice');
  // Campaign screens collapse back to the chooser without permission 27, so a
  // stale screen (or a future caller) can never reach the create forms.
  const activeScreen: ModalScreen =
    !canBuildCampaigns && (screen === 'send-email' || screen === 'send-sms') ? 'choice' : screen;
  const [previewPage, setPreviewPage] = useState(1);
  const [previewSearch, setPreviewSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const pageSize = 25;

  // Form state - Save List
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [listType, setListType] = useState<'static' | 'dynamic'>('static');

  // Form state - Send Email
  const [emailCampaignName, setEmailCampaignName] = useState('');
  const [emailDescription, setEmailDescription] = useState('');
  const [saveAsListEmail, setSaveAsListEmail] = useState(false);
  const [emailListName, setEmailListName] = useState('');
  const [emailListType, setEmailListType] = useState<'static' | 'dynamic'>('static');

  // Form state - Send SMS
  const [smsCampaignName, setSmsCampaignName] = useState('');
  const [smsDescription, setSmsDescription] = useState('');
  const [saveAsListSms, setSaveAsListSms] = useState(false);
  const [smsListName, setSmsListName] = useState('');
  const [smsListType, setSmsListType] = useState<'static' | 'dynamic'>('static');

  // Template picker state
  const [emailTemplatePickerOpen, setEmailTemplatePickerOpen] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<FtpTemplateRow | null>(null);
  const [smsTemplatePickerOpen, setSmsTemplatePickerOpen] = useState(false);
  const [selectedSmsTemplate, setSelectedSmsTemplate] = useState<SmsMarketingTemplate | null>(null);

  useEffect(() => {
    if (isOpen) {
      const ts = formatDateTime(new Date());
      const label = listLabel ?? sourceLabel(sourceType);
      setScreen('choice');
      setPreviewPage(1);
      setPreviewSearch('');
      setSearchInput('');
      setListName(`${label} List - ${ts}`);
      setListDescription('');
      setListType('static');
      setEmailCampaignName(`${label} Campaign - ${ts}`);
      setEmailDescription('');
      setSaveAsListEmail(false);
      setEmailListName(`${label} Campaign - ${ts} - Recipients`);
      setEmailListType('static');
      setSmsCampaignName(`${label} SMS Campaign - ${ts}`);
      setSmsDescription('');
      setSaveAsListSms(false);
      setSmsListName(`${label} SMS Campaign - ${ts} - Recipients`);
      setSmsListType('static');
      setSelectedEmailTemplate(null);
      setSelectedSmsTemplate(null);
    }
  }, [isOpen, sourceType, listLabel]);

  const useExternal = externalRecipientsData !== undefined;

  // Counts query: never includes search — counts reflect the full filtered audience
  const countsPayload = useMemo(() => ({
    sourceType,
    filterConditions,
    filterPlus,
    page: 1,
    perPage: 1,
    search: '',
  }), [sourceType, filterConditions, filterPlus]);

  const { data: countsResult, isLoading: countsLoading, error: countsError } = useQuery({
    queryKey: ['marketing', 'resolve-recipients-counts', sourceType, JSON.stringify(filterConditions), filterPlus],
    queryFn: () => marketingSvc.resolveRecipients(countsPayload),
    enabled: isOpen && !useExternal,
    staleTime: 10_000,
    retry: 1,
  });

  // Table query: includes search + pagination for the preview table
  const tablePayload = useMemo(() => ({
    sourceType,
    filterConditions,
    filterPlus,
    page: previewPage,
    perPage: pageSize,
    search: previewSearch,
  }), [sourceType, filterConditions, filterPlus, previewPage, previewSearch]);

  const { data: tableResult, isFetching, error: tableError } = useQuery({
    queryKey: ['marketing', 'resolve-recipients-table', sourceType, JSON.stringify(filterConditions), filterPlus, previewPage, previewSearch],
    queryFn: () => marketingSvc.resolveRecipients(tablePayload),
    enabled: isOpen && !useExternal,
    staleTime: 10_000,
    retry: 1,
  });

  const resolveError = countsError || tableError;

  useEffect(() => {
    if (resolveError) {
      console.error('[MarketingActionsModal] Resolve recipients failed:', resolveError);
    }
  }, [resolveError]);

  const countsData = useExternal ? externalRecipientsData : countsResult;
  const activeLoading = useExternal ? (externalRecipientsLoading ?? false) : countsLoading;

  const total = countsData?.total ?? 0;
  const uniqueEmails = countsData?.uniqueEmailCount ?? 0;
  const uniquePhones = countsData?.uniquePhoneCount ?? 0;

  const tableData = useExternal ? externalRecipientsData : tableResult;
  const recipients = tableData?.recipients ?? [];
  const tableTotal = tableData?.total ?? 0;
  const totalPages = Math.ceil(tableTotal / pageSize);

  // Auto-generate email list name from campaign name
  useEffect(() => {
    if (emailCampaignName && !emailListName) {
      setEmailListName(`${emailCampaignName} - Recipients`);
    }
  }, [emailCampaignName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (smsCampaignName && !smsListName) {
      setSmsListName(`${smsCampaignName} - Recipients`);
    }
  }, [smsCampaignName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Template selection handlers
  const handleEmailTemplateSelected = useCallback((template: unknown) => {
    setSelectedEmailTemplate(template as FtpTemplateRow);
    setEmailTemplatePickerOpen(false);
  }, []);

  const handleSmsTemplateSelected = useCallback((tpl: SmsMarketingTemplate) => {
    setSelectedSmsTemplate(tpl);
    setSmsTemplatePickerOpen(false);
  }, []);

  // A static list is a snapshot of the audience previewed below: the API
  // resolves these same filters and writes the members when it creates the list.
  const snapshotFields = (type: 'static' | 'dynamic') =>
    type === 'static' ? { sourceType, filterConditions } : {};

  // Mutations
  const saveListMut = useMutation({
    mutationFn: async () => {
      // Active on create: this flow has no detail-page step to activate it, and
      // an inactive list is hidden from the Contact Lists page and the pickers.
      const created = await marketingSvc.createAudienceList({
        name: listName.trim(),
        description: listDescription || undefined,
        listType,
        active: true,
        ...snapshotFields(listType),
      });
      if (listType === 'dynamic' && filterConditions) {
        await marketingSvc.updateAudienceList(created.emailListId, {
          sourceType,
          filterConditions,
        });
      }
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'lists'] });
      toast.success('List saved successfully');
      onClose();
    },
    onError: () => toast.error('Failed to save list'),
  });

  const saveEmailCampaignMut = useMutation({
    mutationFn: async () => {
      if (!canBuildCampaigns) throw new Error('Missing Marketing Campaigns permission');
      const campaign = await campaignsSvc.createCampaign({
        name: emailCampaignName.trim(),
        subject: selectedEmailTemplate?.subject || '',
        description: emailDescription.trim() || null,
        status: 'draft',
      });
      const campaignId = campaign.campaignId;

      if (selectedEmailTemplate) {
        const patch: Record<string, string> = {
          html_content: selectedEmailTemplate.html_content || '',
          plain_content: selectedEmailTemplate.plain_content || '',
        };
        if (selectedEmailTemplate.subject) patch.subject = selectedEmailTemplate.subject;
        if (selectedEmailTemplate.preheader) patch.preheader = selectedEmailTemplate.preheader;
        if (selectedEmailTemplate.from_name) patch.from_name = selectedEmailTemplate.from_name;
        if (selectedEmailTemplate.from_email) patch.from_email = selectedEmailTemplate.from_email;
        if (selectedEmailTemplate.from_email) patch.reply_to = selectedEmailTemplate.from_email;
        if (selectedEmailTemplate.id) patch.email_template_id = selectedEmailTemplate.id;
        await apiClient.patch(`/campaigns/${campaignId}`, patch).catch(() => {});
      }

      if (saveAsListEmail && emailListName.trim()) {
        const list = await marketingSvc.createAudienceList({
          name: emailListName.trim(),
          listType: emailListType,
          ...snapshotFields(emailListType),
        });
        const listUpdateData: Record<string, unknown> = { active: true };
        if (emailListType === 'dynamic' && filterConditions) {
          listUpdateData.sourceType = sourceType;
          listUpdateData.filterConditions = filterConditions;
        }
        await marketingSvc.updateAudienceList(list.emailListId, listUpdateData);
        await campaignsSvc.addCampaignList(campaignId, list.emailListId).catch(() => {});
      }

      return campaignId;
    },
    onSuccess: (campaignId) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'lists'] });
      toast.success('Email campaign created!');
      onClose();
      navigate(`/marketing/campaigns/${campaignId}`);
    },
    onError: () => toast.error('Failed to create email campaign'),
  });

  const saveSmsCampaignMut = useMutation({
    mutationFn: async () => {
      if (!canBuildCampaigns) throw new Error('Missing Marketing Campaigns permission');
      const campaign = await marketingSvc.createSmsCampaign({
        name: smsCampaignName.trim(),
        description: smsDescription.trim() || undefined,
        messageBody: selectedSmsTemplate?.messageBody || undefined,
      });
      const campaignId = campaign.smsCampaignId;

      if (selectedSmsTemplate?.mediaUrls?.length) {
        await marketingSvc.updateSmsCampaign(campaignId, {
          mediaUrls: selectedSmsTemplate.mediaUrls,
        }).catch(() => {});
      }

      if (saveAsListSms && smsListName.trim()) {
        const list = await marketingSvc.createAudienceList({
          name: smsListName.trim(),
          listType: smsListType,
          ...snapshotFields(smsListType),
        });
        const listUpdateData: Record<string, unknown> = { active: true };
        if (smsListType === 'dynamic' && filterConditions) {
          listUpdateData.sourceType = sourceType;
          listUpdateData.filterConditions = filterConditions;
        }
        await marketingSvc.updateAudienceList(list.emailListId, listUpdateData);
        await marketingSvc.addSmsCampaignList(campaignId, list.emailListId).catch(() => {});
      }

      return campaignId;
    },
    onSuccess: (campaignId) => {
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'lists'] });
      toast.success('SMS campaign created!');
      onClose();
      navigate(`/marketing/sms-campaigns/${campaignId}`);
    },
    onError: () => toast.error('Failed to create SMS campaign'),
  });

  const applySearch = () => {
    setPreviewSearch(searchInput.trim());
    setPreviewPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setPreviewSearch('');
    setPreviewPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') applySearch();
  };

  // ─── Shared Recipients Panel (bottom of modal on all screens) ──────

  const renderRecipientsPanel = () => (
    <div className="border-t border-slate-200 bg-slate-50/80 flex flex-col flex-shrink-0">
      {/* Header: counts + search */}
      <div className="flex items-center justify-between gap-4 px-5 py-3 bg-white border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Users className="h-[18px] w-[18px] text-blue-600" />
            <span className="text-sm font-semibold text-slate-700">Unique Contacts</span>
            <span className="bg-blue-600 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full min-w-[20px] text-center">
              {total.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
            <Mail className="h-3 w-3 text-slate-400" />
            <span>{uniqueEmails.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
            <Phone className="h-3 w-3 text-slate-400" />
            <span>{uniquePhones.toLocaleString()}</span>
          </div>
          {activeLoading && <Spinner size="sm" />}
        </div>

        {/* Enter still searches, but users didn't know to press it, hence the button. */}
        <div className="flex flex-shrink-0 items-center gap-2">
          <div className="relative w-[260px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search recipients..."
              aria-label="Search recipients"
              className="w-full h-8 pl-8 pr-7 text-xs border border-slate-200 rounded-md bg-slate-50 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white"
            />
            {searchInput !== '' && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <Button variant="secondary" size="sm" className="text-xs" onClick={applySearch}>
            Search
          </Button>
        </div>
      </div>

      {/* Recipients table. A fixed height rather than a max: a short or empty
          result used to shrink the panel, and the modal resized with it. */}
      <div className="overflow-y-auto bg-white h-[180px] relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
            <Spinner size="sm" />
          </div>
        )}
        <table className="w-full table-fixed text-xs">
          <thead>
            <tr className="sticky top-0 bg-slate-50">
              <th className="w-[30%] px-4 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Email</th>
              <th className="w-[20%] px-4 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">First Name</th>
              <th className="w-[20%] px-4 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Last Name</th>
              <th className="w-[30%] px-4 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Phone</th>
            </tr>
          </thead>
          <tbody>
            {recipients.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-xs">
                  {resolveError ? (
                    <span className="text-red-500">Failed to load recipients. Check browser console for details.</span>
                  ) : previewSearch ? (
                    <span className="text-slate-400">No results match your search</span>
                  ) : (
                    <span className="text-slate-400">No matching recipients</span>
                  )}
                </td>
              </tr>
            ) : (
              recipients.map((r, i) => (
                <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-1.5 text-blue-700 font-medium truncate">{r.email || '--'}</td>
                  <td className="px-4 py-1.5 text-slate-600 truncate">{r.firstName || '--'}</td>
                  <td className="px-4 py-1.5 text-slate-600 truncate">{r.lastName || '--'}</td>
                  <td className="px-4 py-1.5 text-slate-500">{formatPhone(r.phone)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-5 py-2 bg-slate-100/80 border-t border-slate-200 text-xs text-slate-500">
        <span>
          Showing {recipients.length} of {tableTotal.toLocaleString()}{previewSearch ? ' (filtered)' : ''}
        </span>
        {/* Hidden rather than removed on a single page, so the footer keeps its height. */}
        <div className={`flex items-center gap-2 ${totalPages > 1 ? '' : 'invisible'}`}>
          <button
            onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
            disabled={previewPage <= 1}
            className="flex items-center gap-1 px-2.5 py-1 text-xs border border-slate-200 rounded bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-slate-600"
          >
            Prev
          </button>
          <span>{previewPage} / {totalPages}</span>
          <button
            onClick={() => setPreviewPage((p) => Math.min(totalPages, p + 1))}
            disabled={previewPage >= totalPages}
            className="flex items-center gap-1 px-2.5 py-1 text-xs border border-slate-200 rounded bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-slate-600"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Choice Screen ──────────────────────────────────────────────────

  const renderChoiceScreen = () => (
    <div className="px-5 pt-3 pb-5">
      <div className={`grid gap-4 ${canBuildCampaigns ? 'grid-cols-3' : 'grid-cols-1'}`}>
        <button
          type="button"
          onClick={() => setScreen('save-list')}
          className="flex flex-col items-start gap-3 p-5 border-2 border-slate-200 rounded-xl hover:border-blue-400 hover:bg-blue-50/30 transition-all text-left"
        >
          <div className="text-blue-600">
            <Save className="h-8 w-8" />
          </div>
          <span className="text-sm font-semibold text-slate-800">Save List</span>
          <span className="text-[13px] text-slate-500 leading-relaxed">Create a reusable contact list from the current view. Choose between a static snapshot or a dynamic filter-based list.</span>
        </button>
        {canBuildCampaigns && (
          <>
            <button
              type="button"
              onClick={() => setScreen('send-email')}
              className="flex flex-col items-start gap-3 p-5 border-2 border-slate-200 rounded-xl hover:border-green-400 hover:bg-green-50/30 transition-all text-left"
            >
              <div className="text-green-600">
                <Send className="h-8 w-8" />
              </div>
              <span className="text-sm font-semibold text-slate-800">Send Email</span>
              <span className="text-[13px] text-slate-500 leading-relaxed">Send an email campaign to contacts matching the current filters. Select a template and review before sending.</span>
            </button>
            <button
              type="button"
              onClick={() => setScreen('send-sms')}
              className="flex flex-col items-start gap-3 p-5 border-2 border-slate-200 rounded-xl hover:border-purple-400 hover:bg-purple-50/30 transition-all text-left"
            >
              <div className="text-purple-600">
                <MessageSquare className="h-8 w-8" />
              </div>
              <span className="text-sm font-semibold text-slate-800">Send SMS</span>
              <span className="text-[13px] text-slate-500 leading-relaxed">Send an SMS campaign to contacts matching the current filters. Select a template and review before sending.</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  // ─── Save List Screen ───────────────────────────────────────────────

  const renderSaveListScreen = () => (
    <div className="p-5 space-y-5 overflow-y-auto">
      {/* Screen header with back + save */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setScreen('choice')}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="text-lg font-semibold text-slate-900">Save Contact List</h3>
        </div>
        <Button
          disabled={!listName.trim()}
          loading={saveListMut.isPending}
          onClick={() => saveListMut.mutate()}
          leftIcon={<Save className="h-4 w-4" />}
        >
          Save List
        </Button>
      </div>

      <FormField label="List Name" required>
        <Input value={listName} onChange={(e) => setListName(e.target.value)} placeholder="Enter a name for this list..." />
      </FormField>
      <FormField label="Description" labelExtra={<span className="text-xs text-slate-400 font-normal">(optional)</span>}>
        <Textarea autoResize value={listDescription} onChange={(e) => setListDescription(e.target.value)} rows={3} placeholder="Add a description for this list..." />
      </FormField>
      <FormField label="List Type">
        <div className="flex flex-col gap-2.5">
          <label
            className={`flex items-start gap-3 p-3.5 border-2 rounded-lg cursor-pointer transition-all ${
              listType === 'static' ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setListType('static')}
          >
            <input type="radio" name="listType" value="static" checked={listType === 'static'} onChange={() => setListType('static')} className="mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-slate-800">Static List</div>
              <div className="text-xs text-slate-500 mt-0.5">A fixed snapshot of contacts at this moment. The list won't change after it's created.</div>
            </div>
          </label>
          <label
            className={`flex items-start gap-3 p-3.5 border-2 rounded-lg cursor-pointer transition-all ${
              listType === 'dynamic' ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setListType('dynamic')}
          >
            <input type="radio" name="listType" value="dynamic" checked={listType === 'dynamic'} onChange={() => setListType('dynamic')} className="mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-slate-800">Dynamic List</div>
              <div className="text-xs text-slate-500 mt-0.5">Automatically updates based on your filters whenever used. Contacts may change over time.</div>
            </div>
          </label>
        </div>
      </FormField>
    </div>
  );

  // ─── Send Email Screen (two-column: form + template preview) ────────

  const renderSendEmailScreen = () => (
    <div className="p-5 overflow-y-auto">
      {/* Screen header */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-5">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setScreen('choice')}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="text-lg font-semibold text-slate-900">Create Email Campaign</h3>
        </div>
        <Button
          disabled={!emailCampaignName.trim() || !selectedEmailTemplate || saveEmailCampaignMut.isPending || (saveAsListEmail && !emailListName.trim())}
          loading={saveEmailCampaignMut.isPending}
          leftIcon={<Save className="h-4 w-4" />}
          onClick={() => saveEmailCampaignMut.mutate()}
        >
          Save &amp; Review
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left column: form */}
        <div className="space-y-4">
          <FormField label="Campaign Name" required>
            <Input value={emailCampaignName} onChange={(e) => setEmailCampaignName(e.target.value)} placeholder="Enter campaign name..." />
            <p className="text-[11px] text-slate-400 mt-1">A unique name to identify this send in reports.</p>
          </FormField>

          <FormField label="Description" labelExtra={<span className="text-xs text-slate-400 font-normal">(optional)</span>}>
            <Textarea autoResize value={emailDescription} onChange={(e) => setEmailDescription(e.target.value)} rows={3} placeholder="Add a description for this campaign..." />
          </FormField>

          {/* Save as list option */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={saveAsListEmail} onChange={(e) => setSaveAsListEmail(e.target.checked)} className="rounded" />
              <span className="font-semibold text-slate-700">Also save contacts as a reusable list</span>
            </label>
            <p className="text-[11px] text-slate-400 mt-1 ml-6">Contacts will be added directly to this campaign. Check this to also save them as a list for future campaigns.</p>

            {saveAsListEmail && (
              <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                <FormField label="List Name" required>
                  <Input value={emailListName} onChange={(e) => setEmailListName(e.target.value)} />
                </FormField>
                <FormField label="List Type">
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="radio" name="emailListType" value="static" checked={emailListType === 'static'} onChange={() => setEmailListType('static')} className="mt-0.5" />
                      <span>
                        <span className="text-slate-700">Static List</span>
                        <span className="block text-[11px] text-slate-400 mt-0.5">A fixed snapshot of contacts at this moment. The list won't change after it's created.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="radio" name="emailListType" value="dynamic" checked={emailListType === 'dynamic'} onChange={() => setEmailListType('dynamic')} className="mt-0.5" />
                      <span>
                        <span className="text-slate-700">Dynamic List</span>
                        <span className="block text-[11px] text-slate-400 mt-0.5">Automatically updates based on your filters whenever used. Contacts may change over time.</span>
                      </span>
                    </label>
                  </div>
                </FormField>
              </div>
            )}
          </div>
        </div>

        {/* Right column: template preview */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-slate-700">Email Template</span>
            {selectedEmailTemplate && (
              <button
                type="button"
                onClick={() => setEmailTemplatePickerOpen(true)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Change
              </button>
            )}
          </div>
          {selectedEmailTemplate ? (
            <div className="border border-slate-200 rounded-lg bg-white h-[380px] overflow-hidden flex flex-col">
              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex-shrink-0">
                <p className="text-xs font-semibold text-slate-700 truncate">{selectedEmailTemplate.name}</p>
                {selectedEmailTemplate.subject && (
                  <p className="text-[11px] text-slate-400 truncate mt-0.5">Subject: {selectedEmailTemplate.subject}</p>
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                {selectedEmailTemplate.html_content ? (
                  <iframe
                    srcDoc={selectedEmailTemplate.html_content}
                    title="Template preview"
                    className="w-full h-full border-0"
                    sandbox=""
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-xs text-slate-400">No preview available</div>
                )}
              </div>
            </div>
          ) : (
            <div
              onClick={() => setEmailTemplatePickerOpen(true)}
              className="border border-dashed border-slate-300 rounded-lg bg-slate-50 h-[380px] flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
            >
              <div className="flex flex-col items-center gap-3 text-slate-400">
                <Layout className="h-12 w-12 text-slate-300" />
                <span className="text-sm font-medium text-slate-500">Click to select a template</span>
                <span className="text-xs text-slate-400">Choose from your saved email templates</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ─── Send SMS Screen (two-column: form + phone mockup preview) ──────

  const renderSendSmsScreen = () => (
    <div className="p-5 overflow-y-auto">
      {/* Screen header */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-5">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setScreen('choice')}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="text-lg font-semibold text-slate-900">Create SMS Campaign</h3>
        </div>
        <Button
          disabled={!smsCampaignName.trim() || !selectedSmsTemplate || saveSmsCampaignMut.isPending || (saveAsListSms && !smsListName.trim())}
          loading={saveSmsCampaignMut.isPending}
          leftIcon={<Save className="h-4 w-4" />}
          onClick={() => saveSmsCampaignMut.mutate()}
        >
          Save &amp; Review
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left column: form */}
        <div className="space-y-4">
          <FormField label="Campaign Name" required>
            <Input value={smsCampaignName} onChange={(e) => setSmsCampaignName(e.target.value)} placeholder="Enter campaign name..." />
            <p className="text-[11px] text-slate-400 mt-1">A unique name to identify this SMS send in reports.</p>
          </FormField>

          <FormField label="Description" labelExtra={<span className="text-xs text-slate-400 font-normal">(optional)</span>}>
            <Textarea autoResize value={smsDescription} onChange={(e) => setSmsDescription(e.target.value)} rows={3} placeholder="Add a description for this campaign..." />
          </FormField>

          {/* Save as list option */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={saveAsListSms} onChange={(e) => setSaveAsListSms(e.target.checked)} className="rounded" />
              <span className="font-semibold text-slate-700">Also save contacts as a reusable list</span>
            </label>
            <p className="text-[11px] text-slate-400 mt-1 ml-6">Contacts will be added directly to this campaign. Check this to also save them as a list for future campaigns.</p>

            {saveAsListSms && (
              <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                <FormField label="List Name" required>
                  <Input value={smsListName} onChange={(e) => setSmsListName(e.target.value)} />
                </FormField>
                <FormField label="List Type">
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="radio" name="smsListType" value="static" checked={smsListType === 'static'} onChange={() => setSmsListType('static')} className="mt-0.5" />
                      <span>
                        <span className="text-slate-700">Static List</span>
                        <span className="block text-[11px] text-slate-400 mt-0.5">A fixed snapshot of contacts at this moment.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="radio" name="smsListType" value="dynamic" checked={smsListType === 'dynamic'} onChange={() => setSmsListType('dynamic')} className="mt-0.5" />
                      <span>
                        <span className="text-slate-700">Dynamic List</span>
                        <span className="block text-[11px] text-slate-400 mt-0.5">Automatically updates based on your filters whenever used.</span>
                      </span>
                    </label>
                  </div>
                </FormField>
              </div>
            )}
          </div>
        </div>

        {/* Right column: SMS template preview */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-slate-700">SMS Template</span>
            {selectedSmsTemplate && (
              <button
                type="button"
                onClick={() => setSmsTemplatePickerOpen(true)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Change
              </button>
            )}
          </div>
          {selectedSmsTemplate ? (
            <div className="border border-slate-200 rounded-lg bg-white h-[380px] overflow-hidden flex flex-col">
              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex-shrink-0">
                <p className="text-xs font-semibold text-slate-700 truncate">{selectedSmsTemplate.name}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-blue-100 text-slate-800 text-sm rounded-2xl rounded-bl-none px-4 py-3 max-w-[85%] whitespace-pre-wrap">
                  {selectedSmsTemplate.messageBody || 'No message content'}
                </div>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setSmsTemplatePickerOpen(true)}
              className="border border-dashed border-slate-300 rounded-lg bg-slate-50 h-[380px] flex items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/30 transition-all"
            >
              <div className="flex flex-col items-center gap-3 text-slate-400">
                <MessageSquare className="h-12 w-12 text-slate-300" />
                <span className="text-sm font-medium text-slate-500">Click to select a template</span>
                <span className="text-xs text-slate-400">Choose from your saved SMS templates</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ─── Modal ──────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Email / SMS List"
      size="xl"
      bodyClassName="!pt-0"
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Main content area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {activeScreen === 'choice' && renderChoiceScreen()}
          {activeScreen === 'save-list' && renderSaveListScreen()}
          {activeScreen === 'send-email' && renderSendEmailScreen()}
          {activeScreen === 'send-sms' && renderSendSmsScreen()}
        </div>

        {/* Shared recipients panel (always visible at bottom) */}
        {renderRecipientsPanel()}
      </div>

      <FtpTemplatePicker
        isOpen={emailTemplatePickerOpen}
        onClose={() => setEmailTemplatePickerOpen(false)}
        onApply={() => {}}
        mode="select"
        onSelect={handleEmailTemplateSelected}
      />

      <SmsTemplatePickerModal
        isOpen={smsTemplatePickerOpen}
        onClose={() => setSmsTemplatePickerOpen(false)}
        onApply={handleSmsTemplateSelected}
      />
    </Modal>
  );
}
